import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'

import Button from '@space-core/components/button'
import Icon from '@space-core/components/icon'
import Modal from '@space-core/components/modal'

import ModalFooter from '@space-core/components/modalFooter'
import ModalHeader from '@space-core/components/modalHeader'
import { Actions, Entities } from '@space-core/datahub'
import { toastEventDispatch } from '@space-events/toast'
import { ToastPosition } from '@space-events/toast/enum'

import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import {
  ExternalLinkEndpoints,
  ExternalLinkService
} from '@space-shell/common/services/externalLink'
import { queryClient } from '@space-shell/config/request'
import { useMembershipStore } from '@space-shell/core/store'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

export type ExternalLinkModalRemoveProps = {
  isOpen: boolean
  linkId: string
  handleClose: () => void
}

const ExternalLinkModalRemove = ({ isOpen, linkId, handleClose }: ExternalLinkModalRemoveProps) => {
  const { t } = useTranslation()
  const { membershipSlug } = useMembershipStore()
  const { sendDataHubEvent } = useDataHubEvents()

  const { mutate: removeLinkMutate, isLoading } = useMutation(
    () => ExternalLinkService.remove(linkId, membershipSlug),
    {
      onSuccess: async () => {
        sendDataHubEvent({ group: Entities.EXTERNAL_LINKS, name: Actions.REMOVE_SUCCESS })
        await queryClient.invalidateQueries([ExternalLinkEndpoints.list(membershipSlug)])
        handleClose()
      },
      onError: () => {
        toastEventDispatch({
          type: 'danger',
          message: t('external_links.messages.general_error'),
          position: ToastPosition.BOTTOM_RIGHT
        })
      }
    }
  )

  return (
    <Modal show={isOpen} onClose={handleClose}>
      <ModalHeader>
        <div className='_d-flex _align-items-baseline'>
          <h4 className='_text-4'>{t('external_links.modal_remove.title')}</h4>

          <button
            className='_p-0 _d-flex _border-0 _bg-transparent _text-gray-400 _ml-auto _cursor-pointer'
            onClick={handleClose}>
            <span className='_sr-only'>close</span>
            <Icon icon={faXmark} className='_w-5 _h-5' />
          </button>
        </div>

        <p className='_text-gray-500'>{t('external_links.modal_remove.description')}</p>
      </ModalHeader>

      <ModalFooter>
        <div className='_w-full _d-flex _justify-content-end'>
          <Button variation='tertiary' className='_mr-4' onClick={handleClose}>
            {t('general.close')}
          </Button>
          <Button variation='danger' onClick={() => removeLinkMutate()} loading={isLoading}>
            <Icon icon={faTrash} className='_mr-2' />
            {t('external_links.remove')}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default ExternalLinkModalRemove
